// Homepage entry instrumentation. Joining and trying Ember are two different
// decisions, so they share attribution transport but never share an event:
// `marketing.collect_cta_click` is the signup funnel leg, while
// `marketing.guest_trial_click` is the try-first branch.
//
// The CTAs are plain <a href> navigations, so the funnels' click leg has to be
// wired here: fire a keepalive beacon on tap (it survives the navigation) and
// carry the first-touch attribution captured at the /lp | /c landing BOTH on the
// beacon and in the URL. The URL carry matters in in-app webviews (TikTok /
// Facebook) where localStorage is partitioned or disabled — query params are the
// only attribution channel guaranteed to survive a hop, and the page that lands
// forwards them into the verify session so every later leg keeps the same source
// and page cohort.

import { getStoredAttribution, getStoredChannel } from './channel';

/** First-touch attribution as query params: ref = channel slug, pp =
 *  topic/painpoint, platform, sol, plus the whitelisted utm_* bag.
 *
 *  `pid` rides along too: this page load's in-memory id (no cookie, no storage).
 *  The page that lands records it as `source_page_id`, while its own document
 *  gets a fresh `page_id`. That pair lets the funnel follow one page cohort
 *  across a hop without treating an anonymous browser as identity. */
function attributionParams(): URLSearchParams {
  const attrib = getStoredAttribution();
  const channel = attrib?.channel || getStoredChannel();
  const params = new URLSearchParams();
  if (channel) params.set('ref', channel);
  if (attrib?.painpoint) params.set('pp', attrib.painpoint);
  if (attrib?.theme) params.set('theme', attrib.theme);
  if (attrib?.platform) params.set('platform', attrib.platform);
  if (attrib?.solution) params.set('sol', attrib.solution);
  const utm = attrib?.utm;
  if (utm) {
    (['source', 'medium', 'campaign', 'content', 'term'] as const).forEach((k) => {
      const v = utm[k];
      if (v) params.set(`utm_${k}`, v);
    });
  }
  return params;
}

/**
 * Where a join CTA goes: the door, which is the only way in — one text both
 * registers a new handle and signs an existing one in.
 *
 * It is the door's own page — /auth, the same one text by the same shared flow
 * (src/shared-web/inbound-door.ts). Until 2026-09-06 the click was intercepted
 * to raise a dialog in place; that dialog is gone and the href is the whole
 * behaviour. It used to be `/?signin=1`,
 * which landed on the SPA homepage and bounced to /auth from there: two
 * document loads, and the query dropped on the way (2026-08-31).
 *
 * Attribution still rides as query params: in an in-app webview (TikTok /
 * Facebook) localStorage is partitioned or gone, so the URL is the only channel
 * that survives a hop; on this path only the click ids survive, in cookies, read
 * server-side.
 */
export function collectHref(): string {
  const params = attributionParams();
  params.set('returnTo', '/collect');
  return `/auth?${params.toString()}`;
}

/**
 * Where a "Try Ember" CTA in authored content goes: /talk, the same entrance
 * the homepage opens, offering the message and the voice way in.
 *
 * It replaced guestTrialHref() on 2026-09-06. That one entered /collect?trial=1
 * directly — the old guest-trial door — which by then was a funnel no other
 * surface fed: a blog reader who clicked the primary CTA left the path every
 * other visitor walks, and their arrival could not be read beside anyone
 * else's. Whether the conversation needs an account is /talk's decision to
 * make, not this link's.
 *
 * Attribution rides as query params for the reason collectHref explains: in an
 * in-app webview the URL is the only channel first-touch survives on. `pid`
 * remains the marketing page cohort key.
 */
export function talkHref(): string {
  const params = attributionParams();
  const query = params.toString();
  return query ? `/talk?${query}` : '/talk';
}

/**
 * Where the nav's "Sign in" goes: the same door as a page, returning to the
 * homepage freshly loaded (the nav then shows the account) rather than to the
 * conversation chooser. Was `/?signin=1&next=/` until 2026-08-31 — see
 * collectHref above for why that address cost two loads.
 */
export function signInHref(): string {
  const params = attributionParams();
  params.set('returnTo', '/');
  return `/auth?${params.toString()}`;
}

